import { useEffect, useMemo, useState, useRef, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { AlertNotice } from '@/components/ui/alert-notice';
import { useOnboardingForm } from './useOnboardingForm';
import type { FieldConfig } from './useOnboardingForm';
import FileExclusionsCard from './FileExclusionsCard';
import TestScriptSelectionCard from './TestScriptSelectionCard';
import ScriptIndexProgressCard, {
  type ScriptIndexProgress,
} from './ScriptIndexProgressCard';
import ScanCard from './ScanCard';
import { useAppDispatch } from '@/state/store';
import {
  setOnboardingEmails,
  setOnboardingBatchInputs,
  setScmProvider,
} from '@/state/slices/onboardingSlice';
import { assertScmProvider } from '@/types/scm-provider';
import type { ScmProviderId } from '@/types/scm-provider';
import type { PipelinePlatform } from '@/types/pipeline-platform';
import { PIPELINE_PLATFORM_OPTIONS } from '@/types/pipeline-platform';
import { getOrganizationDetails } from '@/components/smartai/api/getOrganizationDetails';
import { getProjectsDetails } from '@/components/smartai/api/getProjectsDetails';
import { getRepositoryDetails } from '@/components/smartai/api/getRepositoryDetails';
import { getBranchesDetails } from '@/components/smartai/api/getBranchDetails';
import { getPipelineDetails } from '@/components/smartai/api/getPipelineDetails';
import { getPipelineStages } from '@/components/smartai/api/getPipelineStages';
import type { PipelineStage } from '@/components/smartai/api/getPipelineStages';
import { getReleasePipelines } from '@/components/smartai/api/getReleasePipelines';
import type { ReleasePipeline } from '@/components/smartai/api/getReleasePipelines';
import { detectRepository } from '@/components/smartai/api/detectRepository';
import type {
  DetectRepositoryBody,
  DetectRepositoryResponse,
  FileItem,
} from '@/components/smartai/api/detectRepository';
import {
  fetchJenkinsJobs,
  type JenkinsJob,
} from '@/components/smartai/api/jenkinsOnboarding';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Combobox } from '@/components/ui/combobox';
import { getTestScriptIndexStatus } from '@/components/smartai/api/getTestScriptIndexStatus';
import type { TestScriptIndexStatusPayload } from '@/components/smartai/api/getTestScriptIndexStatus';
import { getReleaseEnvironments } from '@/components/smartai/api/getReleaseEnvironments';
import type { ReleaseDefinitionEnvironments } from '@/components/smartai/api/getReleaseEnvironments';
import {
  getScriptCandidates,
  type ReleasePipelineSelection,
  type TestScriptCandidate,
} from '@/components/smartai/api/getScriptCandidates';
import { filterNonTestFiles } from '@/utils/filterFilePaths';
import type { CodeAnalysisResult } from '@/utils/filterFilePaths';
import type { organization } from '@/types';
import type { project } from '@/types';
import type { repository } from '@/types';
import type { branch } from '@/types';
import type { pipeline } from '@/types';
import OnboardBgWrapper from '../backgroundImage/OnboardBgWrapper';
import { getApiErrorMessage } from '@/utils';
import OnboardingProviderSelect, {
  type OnboardingProvider,
} from './OnboardingProviderSelect';
import OnboardingPageHeader from './OnboardingPageHeader';
import GitHubOnboardingView from './GitHubOnboardingView';
import type {
  DetectRepoOnboardLocationState,
  DetectRepoOnboardPrefill,
} from '@/components/smartai/DetectRepo/detectRepoOnboardPrefill';
import {
  ApiErrorAlert,
  CascadeSection,
  ChangePlatformButton,
  FooterButtons,
  FormField,
  FormRow,
  InfoAlert,
  OnboardCard,
  OnboardCardBody,
  OnboardCardHeader,
  OnboardingPage,
  PatRow,
  SectionDivider,
  TechStackSection,
  TestScriptLoadSection,
  TestSection,
} from './onboarding-layout';

// ── Zod schema ───────────────────────────────────────────────────────────
// Test-cascade fields are not validated here; we validate them imperatively
// in the submit handler when the test-repo toggle is ON, because useForm's
// resolver is fixed at construction time.

const repositorySetupSchema = z
  .object({
    pat: z.string().min(84, 'PAT must be at least 84 characters').trim(),
    organization: z.string().min(1, 'Please select an organization'),
    project: z.string().min(1, 'Please select a project'),
    repository: z.string().min(1, 'Please select a repository'),
    branch: z.string().min(1, 'Please select a branch'),
    pipeline: z.string().min(1, 'Please select a pipeline'),
    pipelineStage: z.string().min(1, 'Please select a pipeline stage'),
    releasePipeline: z
      .array(z.string())
      .min(1, 'Please select at least one release pipeline'),
    testPat: z.string().min(84, 'PAT must be at least 84 characters').trim(),
    testOrganization: z.string(),
    testProject: z.string(),
    testRepository: z.string(),
    testBranch: z.string(),
    testPipelinePlatform: z.enum(['ado', 'github-actions', 'jenkins']).or(z.literal('')),
    testPipeline: z.string(),
    jenkinsBaseUrl: z.string(),
    jenkinsUsername: z.string(),
    jenkinsApiToken: z.string(),
    jenkinsJobName: z.string(),
    frontendUrl: z
      .string()
      .refine(
        val => val === '' || z.string().url().safeParse(val).success,
        'Enter a valid URL',
      ),
    backendUrl: z
      .string()
      .refine(
        val => val === '' || z.string().url().safeParse(val).success,
        'Enter a valid URL',
      ),
  })
  .refine(data => data.frontendUrl !== '' || data.backendUrl !== '', {
    message: 'At least one URL (Frontend or Backend) is required',
    path: ['frontendUrl'],
  });

type Step1Values = z.infer<typeof repositorySetupSchema>;

const EMPTY_SCRIPT_INDEX_PROGRESS: ScriptIndexProgress = {
  processed: 0,
  total: 0,
  percentage: 0,
};

function buildScriptIndexProgress(
  data: TestScriptIndexStatusPayload,
): ScriptIndexProgress {
  const total = data.totalCount ?? data.selectedCount ?? 0;
  const processed = data.processedCount ?? 0;
  const percentage =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return {
    processed,
    total,
    percentage,
    phase: data.phase,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────

const errMsg = (err: unknown, fallback: string) =>
  err ? getApiErrorMessage(err, fallback) : null;

// ── Main view ────────────────────────────────────────────────────────────

export type OnboardingConnectPrefill = {
  provider: ScmProviderId;
  pat: string;
  organization?: string;
};

type ComponentOnboardingViewProps = {
  connectPrefill?: OnboardingConnectPrefill;
  onExitProvider?: () => void;
};

const ComponentOnboardingView = ({
  connectPrefill,
  onExitProvider,
}: ComponentOnboardingViewProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Capture Detect Repo prefill once from location.state (cleared after bootstrap).
  const [detectPrefill] = useState<DetectRepoOnboardPrefill | undefined>(() => {
    const state = location.state as DetectRepoOnboardLocationState | null;
    return state?.detectRepoPrefill;
  });
  const detectBootstrapRef = useRef(false);
  const adoPrefillRef = useRef<DetectRepoOnboardPrefill | undefined>(
    detectPrefill?.provider === 'azure-devops' ? detectPrefill : undefined,
  );
  const adoPrefillPhaseRef = useRef<
    | 'idle'
    | 'pat-set'
    | 'org-set'
    | 'project-set'
    | 'repo-set'
    | 'branch-set'
    | 'test-pat-set'
    | 'test-org-set'
    | 'test-project-set'
    | 'test-repo-set'
    | 'done'
  >('idle');
  const adoTestPrefillFetchRef = useRef(false);

  const [onboardingProvider, setOnboardingProvider] =
    useState<OnboardingProvider | null>(() =>
      connectPrefill?.provider === 'github' ||
      connectPrefill?.provider === 'azure-devops'
        ? connectPrefill.provider
        : detectPrefill?.provider === 'github' ||
            detectPrefill?.provider === 'azure-devops'
          ? detectPrefill.provider
          : null,
    );

  // ── DB IDs from detectRepository response ────────────────────────────
  const [dbRepoId, setDbRepoId] = useState('');
  const [dbBranchId, setDbBranchId] = useState('');

  // ── PAT / org state ──────────────────────────────────────────────────
  const [patToken, setPatToken] = useState('');
  const [orgList, setOrgList] = useState<organization[]>([]);

  // ── Cascade selected values ──────────────────────────────────────────
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState('');

  // ── Cascade data lists ───────────────────────────────────────────────
  const [projects, setProjects] = useState<project[]>([]);
  const [repos, setRepos] = useState<repository[]>([]);
  const [branches, setBranches] = useState<branch[]>([]);
  const [pipelines, setPipelines] = useState<pipeline[]>([]);
  const [stagesList, setStagesList] = useState<PipelineStage[]>([]);
  const [releasePipelines, setReleasePipelines] = useState<ReleasePipeline[]>(
    [],
  );

  // ── Invalid-PAT fallback (403 → manual org name entry) ───────────────
  const [invalidPatMode, setInvalidPatMode] = useState(false);
  const [invalidTestPatMode, setInvalidTestPatMode] = useState(false);

  // ── Test automation state (only used when testCaseRepo toggle is ON) ─
  const [testCaseRepo, setTestCaseRepo] = useState(false);
  const [testPatToken, setTestPatToken] = useState('');
  const [testOrgList, setTestOrgList] = useState<organization[]>([]);
  const [selectedTestOrg, setSelectedTestOrg] = useState('');
  const [testProjects, setTestProjects] = useState<project[]>([]);
  const [selectedTestProject, setSelectedTestProject] = useState('');
  const [testRepos, setTestRepos] = useState<repository[]>([]);
  const [selectedTestRepo, setSelectedTestRepo] = useState('');
  const [testBranches, setTestBranches] = useState<branch[]>([]);
  const [selectedTestBranch, setSelectedTestBranch] = useState('');
  const [testPipelines, setTestPipelines] = useState<pipeline[]>([]);
  const [jenkinsJobs, setJenkinsJobs] = useState<JenkinsJob[]>([]);
  const [jenkinsJobsLoading, setJenkinsJobsLoading] = useState(false);
  const [jenkinsJobsError, setJenkinsJobsError] = useState<string | null>(null);

  // ── Test script multi-select (when testCaseRepo is on) ─────────────────
  const [scriptCandidates, setScriptCandidates] = useState<
    TestScriptCandidate[]
  >([]);
  const [selectedScriptPaths, setSelectedScriptPaths] = useState<Set<string>>(
    new Set(),
  );
  const [releaseSelections, setReleaseSelections] = useState<
    ReleasePipelineSelection[]
  >([]);
  const [scriptCandidatesLoading, setScriptCandidatesLoading] = useState(false);
  const [scriptCandidatesError, setScriptCandidatesError] = useState<
    string | null
  >(null);
  const [scriptCandidatesLoaded, setScriptCandidatesLoaded] = useState(false);
  const [unresolvedTcCount, setUnresolvedTcCount] = useState(0);

  const [scriptIndexStatus, setScriptIndexStatus] = useState<
    'idle' | 'processing' | 'completed' | 'failed' | 'skipped'
  >('idle');
  const [scriptIndexMessage, setScriptIndexMessage] = useState<string | null>(
    null,
  );
  const [scriptIndexProgress, setScriptIndexProgress] =
    useState<ScriptIndexProgress>(EMPTY_SCRIPT_INDEX_PROGRESS);

  // ── Page-level UI flow flags ─────────────────────────────────────────
  const [showCascadingSelectors, setShowCascadingSelectors] = useState(false);
  const [showTechStack, setShowTechStack] = useState(false);
  const [showFileExclusions, setShowFileExclusions] = useState(false);

  // ── Step 2 state (file analysis) ─────────────────────────────────────
  const [analysisResult, setAnalysisResult] =
    useState<CodeAnalysisResult | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<FileItem>>(new Set());

  // ── Email state (lifted from FileExclusionsCard so Start Full Scan can
  //    validate + dispatch before navigating) ──────────────────────────
  const [prEmails, setPrEmails] = useState<string[]>([]);
  const [autoPrEmails, setAutoPrEmails] = useState<string[]>([]);
  const [skipPrEmails, setSkipPrEmails] = useState(false);
  const [skipAutoPrEmails, setSkipAutoPrEmails] = useState(false);
  const [prEmailsError, setPrEmailsError] = useState('');
  const [autoPrEmailsError, setAutoPrEmailsError] = useState('');

  const orgValueRef = useRef('');
  const testOrgValueRef = useRef('');
  const exclusionsRef = useRef<HTMLDivElement | null>(null);

  function scrollToExclusions(e?: MouseEvent) {
    e?.preventDefault();
    exclusionsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  // ── Mutations ─────────────────────────────────────────────────────────

  // Set when Fetch Organizations succeeds and Detect Repo prefill should run.
  const adoApplyAfterFetchRef = useRef(false);

  const orgMutation = useMutation({
    mutationFn: (pat: string) =>
      getOrganizationDetails(assertScmProvider(onboardingProvider), pat),
    onSuccess: data => {
      setInvalidPatMode(false);
      setOrgList(data?.data?.value ?? []);
      setShowCascadingSelectors(true);
      if (
        adoPrefillRef.current &&
        adoPrefillPhaseRef.current === 'pat-set'
      ) {
        adoApplyAfterFetchRef.current = true;
      }
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) setInvalidPatMode(true);
      setShowCascadingSelectors(true);
    },
  });

  // organization account id
  const orgAccountId = useRef<string>('');
  const testOrgAccountId = useRef<string>('');

  const projectsMutation = useMutation({
    mutationFn: ({ org }: { org: string }) =>
      getProjectsDetails({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org },
        requesterToken: { patToken },
      }),
    onSuccess: data => {
      orgAccountId.current = data?.data.organization.instanceId;
      setProjects(data?.data?.projects?.value ?? []);
    },
  });

  const reposMutation = useMutation({
    mutationFn: ({ org, proj }: { org: string; proj: string }) =>
      getRepositoryDetails({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org, project: proj },
        requesterToken: { patToken },
      }),
    onSuccess: data => {
      setRepos(data?.data?.value ?? []);
    },
  });

  const branchesMutation = useMutation({
    mutationFn: ({
      org,
      proj,
      repoId,
    }: {
      org: string;
      proj: string;
      repoId: string;
    }) =>
      getBranchesDetails(
        assertScmProvider(onboardingProvider),
        { organization: org, project: proj, repositoryId: repoId },
        { patToken },
      ),
    onSuccess: data => {
      setBranches(data?.data?.value ?? []);
    },
  });

  const pipelinesMutation = useMutation({
    mutationFn: ({ org, proj }: { org: string; proj: string }) =>
      getPipelineDetails({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org, project: proj },
        requesterToken: { patToken },
      }),
    onSuccess: data => setPipelines(data ?? []),
  });

  const stagesMutation = useMutation({
    mutationFn: ({
      org,
      proj,
      pipelineId,
    }: {
      org: string;
      proj: string;
      pipelineId: string;
      orgUrl: string;
    }) =>
      getPipelineStages({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org, project: proj, pipelineId, orgUrl: '' },
        requesterToken: { patToken },
      }),
    onSuccess: data => setStagesList(data?.data?.stages ?? []),
  });

  const releasePipelinesMutation = useMutation({
    mutationFn: ({
      org,
      proj,
    }: {
      org: string;
      proj: string;
      orgUrl: string;
    }) =>
      getReleasePipelines({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org, project: proj, orgUrl: '' },
        requesterToken: { patToken },
      }),
    onSuccess: data => setReleasePipelines(data?.data?.pipelines ?? []),
  });

  const detectMutation = useMutation({
    mutationFn: (body: DetectRepositoryBody) =>
      detectRepository(assertScmProvider(onboardingProvider), body, patToken),
  });

  // ── Test automation mutations (same API, separate PAT) ───────────────

  const testOrgMutation = useMutation({
    mutationFn: (pat: string) =>
      getOrganizationDetails(assertScmProvider(onboardingProvider), pat),
    onSuccess: data => {
      setInvalidTestPatMode(false);
      setTestOrgList(data?.data?.value ?? []);
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) setInvalidTestPatMode(true);
    },
  });

  const testProjectsMutation = useMutation({
    mutationFn: ({ org }: { org: string }) =>
      getProjectsDetails({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org },
        requesterToken: { patToken: testPatToken },
      }),
    onSuccess: data => {
      testOrgAccountId.current = data?.data?.organization.instanceId;
      setTestProjects(data?.data?.projects?.value ?? []);
    },
  });

  const testReposMutation = useMutation({
    mutationFn: ({ org, proj }: { org: string; proj: string }) =>
      getRepositoryDetails({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org, project: proj },
        requesterToken: { patToken: testPatToken },
      }),
    onSuccess: (data, { proj }) => {
      setTestRepos(data?.data?.value ?? []);
      setSelectedTestProject(proj);
    },
  });

  const testBranchesMutation = useMutation({
    mutationFn: ({
      org,
      proj,
      repoId,
    }: {
      org: string;
      proj: string;
      repoId: string;
    }) =>
      getBranchesDetails(
        assertScmProvider(onboardingProvider),
        { organization: org, project: proj, repositoryId: repoId },
        { patToken: testPatToken },
      ),
    onSuccess: (data, { repoId }) => {
      setTestBranches(data?.data?.value ?? []);
      setSelectedTestRepo(repoId);
    },
  });

  const testPipelinesMutation = useMutation({
    mutationFn: ({ org, proj }: { org: string; proj: string }) =>
      getPipelineDetails({
        provider: assertScmProvider(onboardingProvider),
        body: { organization: org, project: proj },
        requesterToken: { patToken: testPatToken },
      }),
    onSuccess: data => setTestPipelines(data ?? []),
  });

  // ── Field config ─────────────────────────────────────────────────────

  const step1Fields = useMemo<FieldConfig<Step1Values>[]>(
    () => [
      {
        name: 'pat',
        label: 'Personal Access Token (PAT)',
        type: 'password',
        placeholder: 'Enter your PAT',
        layout: 'pat',
      },
      {
        name: 'organization',
        label: 'Organization',
        ...(invalidPatMode
          ? {
              type: 'text' as const,
              placeholder: 'Enter your organization name manually',
              actionButton: (
                <Button
                  variant="outline"
                  type="button"
                  disabled={projectsMutation.isPending}
                  onClick={handleGetProjectsManualOrg}>
                  {projectsMutation.isPending ? 'Fetching…' : 'Get Projects'}
                </Button>
              ),
            }
          : {
              type: 'combobox' as const,
              options: orgList.map(o => ({
                value: o.accountName,
                label: o.accountName,
              })),
              disabled: orgList.length === 0 || orgMutation.isPending,
              loading: orgMutation.isPending,
              placeholder:
                orgList.length === 0
                  ? 'Fetch organizations first'
                  : 'Select Organization',
            }),
      },
      {
        name: 'project',
        label: 'Project',
        type: 'combobox',
        options: projects.map(p => ({ value: p.name, label: p.name })),
        disabled: !selectedOrg || projectsMutation.isPending,
        loading: projectsMutation.isPending,
        placeholder: !selectedOrg
          ? 'Select an organization first'
          : 'Select Project',
      },
      {
        name: 'repository',
        label: 'Repository',
        type: 'combobox',
        options: repos.map(r => ({ value: r.id, label: r.name })),
        disabled: !selectedProject || reposMutation.isPending,
        loading: reposMutation.isPending,
        placeholder: !selectedProject
          ? 'Select a project first'
          : 'Select Repository',
      },
      {
        name: 'branch',
        label: 'Branch',
        type: 'combobox',
        options: branches.map(b => ({ value: b.name, label: b.name })),
        disabled: !selectedRepo || branchesMutation.isPending,
        loading: branchesMutation.isPending,
        placeholder: !selectedRepo
          ? 'Select a repository first'
          : 'Select Branch',
      },
      {
        name: 'pipeline',
        label: 'Pipeline',
        type: 'combobox',
        options: pipelines.map(p => ({ value: String(p.id), label: p.name })),
        disabled: !selectedBranch || pipelinesMutation.isPending,
        loading: pipelinesMutation.isPending,
        placeholder: !selectedBranch
          ? 'Select a branch first'
          : 'Select Pipeline',
      },
      {
        name: 'pipelineStage',
        label: 'Pipeline Stage',
        type: 'combobox',
        options: stagesList.map(s => ({ value: s.name, label: s.name })),
        disabled: !selectedPipeline || stagesMutation.isPending,
        loading: stagesMutation.isPending,
        placeholder: !selectedPipeline
          ? 'Select a pipeline first'
          : 'Select Pipeline Stage',
      },
      {
        name: 'releasePipeline',
        label: 'Release Pipeline',
        type: 'combobox',
        multiple: true,
        options: releasePipelines.map(p => ({
          value: String(p.id),
          label: p.name,
        })),
        disabled: !selectedPipeline || releasePipelinesMutation.isPending,
        loading: releasePipelinesMutation.isPending,
        placeholder: !selectedPipeline
          ? 'Select a pipeline first'
          : 'Select Release Pipeline(s)',
      },
      {
        name: 'testPat',
        label: 'Personal Access Token (Test Automation)',
        type: 'password',
        placeholder: 'Enter your test PAT',
        layout: 'pat',
      },
      {
        name: 'testOrganization',
        label: 'Test Organization',
        ...(invalidTestPatMode
          ? {
              type: 'text' as const,
              placeholder: 'Enter your test organization name manually',
              actionButton: (
                <Button
                  variant="outline"
                  type="button"
                  disabled={testProjectsMutation.isPending}
                  onClick={handleGetTestProjectsManualOrg}>
                  {testProjectsMutation.isPending
                    ? 'Fetching…'
                    : 'Get Projects'}
                </Button>
              ),
            }
          : {
              type: 'combobox' as const,
              options: testOrgList.map(o => ({
                value: o.accountName,
                label: o.accountName,
              })),
              disabled: testOrgList.length === 0 || testOrgMutation.isPending,
              loading: testOrgMutation.isPending,
              placeholder:
                testOrgList.length === 0
                  ? 'Fetch test organizations first'
                  : 'Select Test Organization',
            }),
      },
      {
        name: 'testProject',
        label: 'Test Project',
        type: 'combobox',
        options: testProjects.map(p => ({ value: p.name, label: p.name })),
        disabled: !selectedTestOrg || testProjectsMutation.isPending,
        loading: testProjectsMutation.isPending,
        placeholder: !selectedTestOrg
          ? 'Select a test organization first'
          : 'Select Test Project',
      },
      {
        name: 'testRepository',
        label: 'Test Repository',
        type: 'combobox',
        options: testRepos.map(r => ({ value: r.id, label: r.name })),
        disabled: !selectedTestProject || testReposMutation.isPending,
        loading: testReposMutation.isPending,
        placeholder: !selectedTestProject
          ? 'Select a test project first'
          : 'Select Test Repository',
      },
      {
        name: 'testBranch',
        label: 'Test Branch',
        type: 'combobox',
        options: testBranches.map(b => ({ value: b.name, label: b.name })),
        disabled: !selectedTestRepo || testBranchesMutation.isPending,
        loading: testBranchesMutation.isPending,
        placeholder: !selectedTestRepo
          ? 'Select a test repository first'
          : 'Select Test Branch',
      },
      {
        name: 'testPipelinePlatform',
        label: 'Pipeline Platform',
        type: 'combobox',
        options: PIPELINE_PLATFORM_OPTIONS.map(o => ({
          value: o.value,
          label: o.label,
        })),
        disabled: !selectedTestBranch,
        placeholder: !selectedTestBranch
          ? 'Select a test branch first'
          : 'Select Pipeline Platform',
      },
      {
        name: 'testPipeline',
        label: 'Test Pipeline',
        type: 'combobox',
        options: testPipelines.map(p => ({
          value: String(p.id),
          label: p.name,
        })),
        disabled: !selectedTestBranch || testPipelinesMutation.isPending,
        loading: testPipelinesMutation.isPending,
        placeholder: !selectedTestBranch
          ? 'Select a test branch first'
          : 'Select Test Pipeline',
      },
      {
        name: 'jenkinsBaseUrl',
        label: 'Jenkins URL',
        type: 'text',
        placeholder: 'e.g. http://13.205.65.111:8080',
      },
      {
        name: 'jenkinsUsername',
        label: 'Jenkins Username',
        type: 'text',
        placeholder: 'Jenkins login username',
      },
      {
        name: 'jenkinsApiToken',
        label: 'Jenkins API Token',
        type: 'password',
        placeholder: 'API token for this user',
      },
      {
        name: 'jenkinsJobName',
        label: 'Jenkins Job',
        type: 'combobox',
        options: jenkinsJobs.map(j => ({
          value: j.fullName,
          label: j.fullName,
        })),
        disabled: jenkinsJobs.length === 0 || jenkinsJobsLoading,
        loading: jenkinsJobsLoading,
        placeholder:
          jenkinsJobs.length === 0
            ? 'Fetch Jenkins jobs first'
            : 'Select Jenkins Job',
      },
      {
        name: 'frontendUrl',
        label: 'Frontend URL',
        type: 'text',
        placeholder: 'e.g. https://app.example.com',
      },
      {
        name: 'backendUrl',
        label: 'Backend URL',
        type: 'text',
        placeholder: 'e.g. https://api.example.com',
      },
    ],
    [
      orgList,
      projects,
      repos,
      branches,
      pipelines,
      stagesList,
      releasePipelines,
      selectedOrg,
      selectedProject,
      selectedRepo,
      selectedBranch,
      selectedPipeline,
      invalidPatMode,
      orgMutation.isPending,
      projectsMutation.isPending,
      reposMutation.isPending,
      branchesMutation.isPending,
      pipelinesMutation.isPending,
      stagesMutation.isPending,
      releasePipelinesMutation.isPending,
      testOrgList,
      testProjects,
      testRepos,
      testBranches,
      testPipelines,
      jenkinsJobs,
      jenkinsJobsLoading,
      selectedTestOrg,
      selectedTestProject,
      selectedTestRepo,
      selectedTestBranch,
      invalidTestPatMode,
      testOrgMutation.isPending,
      testProjectsMutation.isPending,
      testReposMutation.isPending,
      testBranchesMutation.isPending,
      testPipelinesMutation.isPending,
    ],
  );

  // ── Form hook ────────────────────────────────────────────────────────

  const {
    renderFields,
    onSubmit,
    isSubmitting,
    watch,
    setValue,
    reset: resetForm,
    setError,
  } = useOnboardingForm<Step1Values>({
    fields: step1Fields,
    defaultValues: {
      pat: connectPrefill?.pat ?? '',
      organization: '',
      project: '',
      repository: '',
      branch: '',
      pipeline: '',
      pipelineStage: '',
      releasePipeline: [],
      testPat: '',
      testOrganization: '',
      testProject: '',
      testRepository: '',
      testBranch: '',
      testPipelinePlatform: '',
      testPipeline: '',
      jenkinsBaseUrl: '',
      jenkinsUsername: '',
      jenkinsApiToken: '',
      jenkinsJobName: '',
      frontendUrl: '',
      backendUrl: '',
    },
    schema: repositorySetupSchema,
    onSubmit: handleDetectRepository,
  });

  const patValue = watch('pat');
  const orgValue = watch('organization');
  const projectValue = watch('project');
  const repoValue = watch('repository');
  const branchValue = watch('branch');
  const pipelineValue = watch('pipeline');
  const testPatValue = watch('testPat');
  const testOrgValue = watch('testOrganization');
  const testProjectValue = watch('testProject');
  const testRepoValue = watch('testRepository');
  const testBranchValue = watch('testBranch');
  const testPipelinePlatformValue = watch('testPipelinePlatform');
  const testPipelineValue = watch('testPipeline');
  const jenkinsBaseUrlValue = watch('jenkinsBaseUrl');
  const jenkinsUsernameValue = watch('jenkinsUsername');
  const jenkinsApiTokenValue = watch('jenkinsApiToken');
  const jenkinsJobNameValue = watch('jenkinsJobName');
  const releasePipelineValue = watch('releasePipeline');

  orgValueRef.current = orgValue;
  testOrgValueRef.current = testOrgValue;

  // ── Sync PAT into state when set via setValue() during reset, etc. ────

  useEffect(() => {
    setPatToken(patValue);
  }, [patValue]);

  useEffect(() => {
    setTestPatToken(testPatValue);
  }, [testPatValue]);

  useEffect(() => {
    if (connectPrefill) {
      dispatch(setScmProvider(connectPrefill.provider));
    }
  }, [connectPrefill, dispatch]);

  const adoConnectFetchedRef = useRef(false);
  useEffect(() => {
    if (
      !connectPrefill ||
      connectPrefill.provider !== 'azure-devops' ||
      onboardingProvider !== 'azure-devops'
    ) {
      return;
    }
    if (adoConnectFetchedRef.current) return;
    if (!patValue || patValue.length < 5) return;
    adoConnectFetchedRef.current = true;
    handleFetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectPrefill, onboardingProvider, patValue]);

  useEffect(() => {
    if (
      !connectPrefill?.organization ||
      connectPrefill.provider !== 'azure-devops' ||
      onboardingProvider !== 'azure-devops' ||
      orgList.length === 0
    ) {
      return;
    }
    if (!orgList.some(org => org.accountName === connectPrefill.organization)) {
      return;
    }
    if (orgValue === connectPrefill.organization) return;
    setValue('organization', connectPrefill.organization);
  }, [
    connectPrefill,
    onboardingProvider,
    orgList,
    orgValue,
    setValue,
  ]);

  // ── Detect Repo bootstrap: sync Redux + clear location state ────────

  useEffect(() => {
    if (detectBootstrapRef.current || !detectPrefill) return;
    if (
      detectPrefill.provider !== 'github' &&
      detectPrefill.provider !== 'azure-devops'
    ) {
      return;
    }
    detectBootstrapRef.current = true;
    dispatch(setScmProvider(detectPrefill.provider));
    navigate(location.pathname, { replace: true, state: {} });
  }, [detectPrefill, dispatch, location.pathname, navigate]);

  // Prefill ADO PAT as soon as the Azure DevOps form is mounted.
  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      onboardingProvider !== 'azure-devops' ||
      !pf ||
      adoPrefillPhaseRef.current !== 'idle'
    ) {
      return;
    }
    adoPrefillPhaseRef.current = 'pat-set';
    setValue('pat', pf.pat);
  }, [onboardingProvider, setValue]);

  // After Fetch Organizations: autofill application org cascade; open test section.
  useEffect(() => {
    if (!showCascadingSelectors || !adoApplyAfterFetchRef.current) return;
    const pf = adoPrefillRef.current;
    if (!pf || adoPrefillPhaseRef.current !== 'pat-set') return;
    adoApplyAfterFetchRef.current = false;
    adoPrefillPhaseRef.current = 'org-set';
    setValue('organization', pf.organization);
    setTestCaseRepo(true);
  }, [showCascadingSelectors, setValue]);

  // Walk project → repo → branch as each list loads (code repo only).
  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      !pf?.project ||
      adoPrefillPhaseRef.current !== 'org-set' ||
      projects.length === 0
    ) {
      return;
    }
    if (projects.some(p => p.name === pf.project)) {
      adoPrefillPhaseRef.current = 'project-set';
      setValue('project', pf.project);
    }
  }, [projects, setValue]);

  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      !pf?.repository ||
      adoPrefillPhaseRef.current !== 'project-set' ||
      repos.length === 0
    ) {
      return;
    }
    const match = repos.find(r => r.name === pf.repository);
    if (match) {
      adoPrefillPhaseRef.current = 'repo-set';
      setValue('repository', match.id);
    }
  }, [repos, setValue]);

  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      adoPrefillPhaseRef.current !== 'repo-set' ||
      branches.length === 0
    ) {
      return;
    }
    const wanted = pf?.branch?.trim();
    if (wanted && branches.some(b => b.name === wanted)) {
      setValue('branch', wanted);
    }

    // Application cascade done — start test-repo prefill when present.
    if (pf?.testRepository || pf?.testOrganization) {
      const testToken = (pf.testPat || pf.pat).trim();
      setValue('testPat', testToken);
      adoPrefillPhaseRef.current = 'test-pat-set';
      adoTestPrefillFetchRef.current = true;
    } else {
      adoPrefillPhaseRef.current = 'done';
    }
  }, [branches, setValue]);

  // Fetch test orgs once application prefill finishes (Onboard Both).
  useEffect(() => {
    if (!adoTestPrefillFetchRef.current) return;
    if (adoPrefillPhaseRef.current !== 'test-pat-set') return;
    const pf = adoPrefillRef.current;
    if (!pf) return;
    const testToken = (pf.testPat || pf.pat).trim();
    if (!testToken || testToken.length < 5) {
      adoPrefillPhaseRef.current = 'done';
      adoTestPrefillFetchRef.current = false;
      return;
    }
    adoTestPrefillFetchRef.current = false;
    testOrgMutation.mutate(testToken);
  }, [testPatValue]);

  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      !pf ||
      adoPrefillPhaseRef.current !== 'test-pat-set' ||
      testOrgList.length === 0
    ) {
      return;
    }
    const orgName = pf.testOrganization || pf.organization;
    if (testOrgList.some(o => o.accountName === orgName)) {
      adoPrefillPhaseRef.current = 'test-org-set';
      setValue('testOrganization', orgName);
    }
  }, [testOrgList, setValue]);

  useEffect(() => {
    const pf = adoPrefillRef.current;
    const projectName = pf?.testProject || pf?.project;
    if (
      !projectName ||
      adoPrefillPhaseRef.current !== 'test-org-set' ||
      testProjects.length === 0
    ) {
      return;
    }
    if (testProjects.some(p => p.name === projectName)) {
      adoPrefillPhaseRef.current = 'test-project-set';
      setValue('testProject', projectName);
    }
  }, [testProjects, setValue]);

  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      !pf?.testRepository ||
      adoPrefillPhaseRef.current !== 'test-project-set' ||
      testRepos.length === 0
    ) {
      return;
    }
    const match = testRepos.find(r => r.name === pf.testRepository);
    if (match) {
      adoPrefillPhaseRef.current = 'test-repo-set';
      setValue('testRepository', match.id);
    }
  }, [testRepos, setValue]);

  useEffect(() => {
    const pf = adoPrefillRef.current;
    if (
      adoPrefillPhaseRef.current !== 'test-repo-set' ||
      testBranches.length === 0
    ) {
      return;
    }
    const wanted = pf?.testBranch?.trim();
    if (wanted && testBranches.some(b => b.name === wanted)) {
      setValue('testBranch', wanted);
    }
    adoPrefillPhaseRef.current = 'done';
  }, [testBranches, setValue]);

  // ── Main cascade effects ─────────────────────────────────────────────

  useEffect(() => {
    if (!orgValue || orgValue === selectedOrg || invalidPatMode) return;
    setSelectedOrg(orgValue);
    setSelectedProject('');
    setSelectedRepo('');
    setSelectedBranch('');
    setSelectedPipeline('');
    setProjects([]);
    setRepos([]);
    setBranches([]);
    setPipelines([]);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('project', '');
    setValue('repository', '');
    setValue('branch', '');
    setValue('pipeline', '');
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
    projectsMutation.mutate({ org: orgValue });
  }, [orgValue]);

  useEffect(() => {
    if (!projectValue || projectValue === selectedProject || !selectedOrg)
      return;
    setSelectedProject(projectValue);
    setSelectedRepo('');
    setSelectedBranch('');
    setSelectedPipeline('');
    setRepos([]);
    setBranches([]);
    setPipelines([]);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('repository', '');
    setValue('branch', '');
    setValue('pipeline', '');
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
    reposMutation.mutate({ org: selectedOrg, proj: projectValue });
  }, [projectValue]);

  useEffect(() => {
    if (
      !repoValue ||
      repoValue === selectedRepo ||
      !selectedOrg ||
      !selectedProject
    )
      return;
    setSelectedRepo(repoValue);
    setSelectedBranch('');
    setSelectedPipeline('');
    setBranches([]);
    setPipelines([]);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('branch', '');
    setValue('pipeline', '');
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
    branchesMutation.mutate({
      org: selectedOrg,
      proj: selectedProject,
      repoId: repoValue,
    });
  }, [repoValue]);

  useEffect(() => {
    if (
      !branchValue ||
      branchValue === selectedBranch ||
      !selectedOrg ||
      !selectedProject
    )
      return;
    setSelectedBranch(branchValue);
    setSelectedPipeline('');
    setPipelines([]);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('pipeline', '');
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
    pipelinesMutation.mutate({ org: selectedOrg, proj: selectedProject });
  }, [branchValue]);

  useEffect(() => {
    if (
      !pipelineValue ||
      pipelineValue === selectedPipeline ||
      !selectedOrg ||
      !selectedProject
    )
      return;
    setSelectedPipeline(pipelineValue);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
    const orgUrl =
      orgList.find(o => o.accountName === selectedOrg)?.accountUri ?? '';
    stagesMutation.mutate({
      org: selectedOrg,
      proj: selectedProject,
      pipelineId: pipelineValue,
      orgUrl,
    });
    releasePipelinesMutation.mutate({
      org: selectedOrg,
      proj: selectedProject,
      orgUrl,
    });
  }, [pipelineValue]);

  // ── Test automation cascade effects ──────────────────────────────────

  useEffect(() => {
    if (!testOrgValue || testOrgValue === selectedTestOrg || invalidTestPatMode)
      return;
    setSelectedTestOrg(testOrgValue);
    setSelectedTestProject('');
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setTestProjects([]);
    setTestRepos([]);
    setTestBranches([]);
    setTestPipelines([]);
    setValue('testProject', '');
    setValue('testRepository', '');
    setValue('testBranch', '');
    setValue('testPipeline', '');
    testProjectsMutation.mutate({ org: testOrgValue });
  }, [testOrgValue]);

  useEffect(() => {
    if (
      !testProjectValue ||
      testProjectValue === selectedTestProject ||
      !selectedTestOrg
    )
      return;
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setTestRepos([]);
    setTestBranches([]);
    setTestPipelines([]);
    setValue('testRepository', '');
    setValue('testBranch', '');
    setValue('testPipeline', '');
    testReposMutation.mutate({ org: selectedTestOrg, proj: testProjectValue });
  }, [testProjectValue]);

  useEffect(() => {
    if (
      !testRepoValue ||
      testRepoValue === selectedTestRepo ||
      !selectedTestOrg ||
      !selectedTestProject
    )
      return;
    setSelectedTestBranch('');
    setTestBranches([]);
    setTestPipelines([]);
    setValue('testBranch', '');
    setValue('testPipeline', '');
    testBranchesMutation.mutate({
      org: selectedTestOrg,
      proj: selectedTestProject,
      repoId: testRepoValue,
    });
  }, [testRepoValue]);

  useEffect(() => {
    if (
      !testBranchValue ||
      testBranchValue === selectedTestBranch ||
      !selectedTestOrg ||
      !selectedTestProject
    )
      return;
    setSelectedTestBranch(testBranchValue);
    setTestPipelines([]);
    setValue('testPipeline', '');
    setValue('testPipelinePlatform', '');
    setValue('jenkinsJobName', '');
    setJenkinsJobs([]);
    setJenkinsJobsError(null);
  }, [testBranchValue]);

  useEffect(() => {
    if (!testPipelinePlatformValue) return;
    // Clear downstream CI fields when platform changes.
    setTestPipelines([]);
    setValue('testPipeline', '');
    setValue('jenkinsJobName', '');
    setJenkinsJobs([]);
    setJenkinsJobsError(null);

    if (
      testPipelinePlatformValue === 'ado' &&
      selectedTestBranch &&
      selectedTestOrg &&
      selectedTestProject
    ) {
      testPipelinesMutation.mutate({
        org: selectedTestOrg,
        proj: selectedTestProject,
      });
    }
  }, [testPipelinePlatformValue]);

  async function handleFetchJenkinsJobs() {
    setJenkinsJobsError(null);
    setJenkinsJobs([]);
    setValue('jenkinsJobName', '');
    const baseUrl = jenkinsBaseUrlValue?.trim() ?? '';
    const username = jenkinsUsernameValue?.trim() ?? '';
    const apiToken = jenkinsApiTokenValue?.trim() ?? '';
    if (!baseUrl || !username || !apiToken) {
      setJenkinsJobsError('Jenkins URL, username, and API token are required.');
      return;
    }
    setJenkinsJobsLoading(true);
    try {
      const res = await fetchJenkinsJobs({ baseUrl, username, apiToken });
      const jobs = res.data?.jobs ?? [];
      setJenkinsJobs(jobs);
      if (jobs.length === 0) {
        setJenkinsJobsError('No Jenkins jobs found for these credentials.');
      }
    } catch (e) {
      setJenkinsJobsError(getApiErrorMessage(e, 'Failed to fetch Jenkins jobs'));
    } finally {
      setJenkinsJobsLoading(false);
    }
  }

  function resetScriptState() {
    setScriptCandidates([]);
    setSelectedScriptPaths(new Set());
    setReleaseSelections([]);
    setScriptCandidatesLoaded(false);
    setScriptCandidatesError(null);
    setUnresolvedTcCount(0);
    setScriptIndexStatus('idle');
    setScriptIndexMessage(null);
    setScriptIndexProgress(EMPTY_SCRIPT_INDEX_PROGRESS);
  }

  useEffect(() => {
    if (!testCaseRepo) return;
    resetScriptState();
  }, [
    releasePipelineValue,
    testBranchValue,
    testRepoValue,
    testProjectValue,
    testOrgValue,
    testCaseRepo,
  ]);

  useEffect(() => {
    if (
      !showFileExclusions ||
      !testCaseRepo ||
      !dbRepoId ||
      scriptIndexStatus !== 'processing' ||
      !patToken
    )
      return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getTestScriptIndexStatus(dbRepoId);
        if (cancelled) return;
        const data = res.data;
        if (!res.success || !data) return;

        const st = String(data.status ?? '').toLowerCase();
        if (st === 'completed') {
          setScriptIndexStatus('completed');
          setScriptIndexMessage(
            data.message ?? `Indexed ${data.inserted ?? 0} test script(s)`,
          );
          setScriptIndexProgress(buildScriptIndexProgress(data));
        } else if (st === 'failed') {
          setScriptIndexStatus('failed');
          setScriptIndexMessage(
            data.error || data.message || 'Test script indexing failed',
          );
        } else if (st === 'processing' || st === 'running') {
          setScriptIndexMessage(
            data.message ?? 'Indexing test scripts in background…',
          );
          setScriptIndexProgress(buildScriptIndexProgress(data));
        }
      } catch {
        if (!cancelled) {
          setScriptIndexMessage(
            'Could not refresh test script index status. Retrying…',
          );
        }
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dbRepoId, scriptIndexStatus, patToken, showFileExclusions, testCaseRepo]);

  function buildReleaseSelectionsFromEnvironments(
    defs: ReleaseDefinitionEnvironments[],
  ): ReleasePipelineSelection[] {
    return defs.map(def => {
      const withHint = (def.environments ?? []).filter(e => e.hasKatalonHint);
      const pick = withHint.length ? withHint : (def.environments ?? []);
      const envNames = pick.map(e => e.name).filter(Boolean);
      return {
        releaseDefinitionId: def.releaseDefinitionId,
        releasePipelineName: def.releasePipelineName,
        environmentNames: envNames.length ? envNames : undefined,
      };
    });
  }

  async function handleLoadTestScripts() {
    const releaseIds = (releasePipelineValue ?? [])
      .map(id => parseInt(String(id), 10))
      .filter(id => !Number.isNaN(id));
    const testRepoObj = testRepos.find(r => r.id === testRepoValue);
    const testUrl = testRepoObj?.remoteUrl?.trim() ?? '';
    const branchName = (testBranchValue || '').trim();
    const listPat = (testPatToken || patToken).trim();

    if (!patToken || !selectedOrg || !selectedProject) {
      setScriptCandidatesError(
        'Complete main PAT, organization, and project first.',
      );
      return;
    }
    if (!releaseIds.length) {
      setScriptCandidatesError('Select at least one release pipeline.');
      return;
    }
    if (!testUrl || !branchName) {
      setScriptCandidatesError('Select test repository and branch.');
      return;
    }
    if (!testOrgValue || !testProjectValue) {
      setScriptCandidatesError('Select test organization and project.');
      return;
    }

    const selectedReleasePipelineObjs = releasePipelines.filter(p =>
      releaseIds.includes(
        typeof p.id === 'number' ? p.id : parseInt(String(p.id), 10),
      ),
    );
    const pipelineNames: Record<string, string> = {};
    for (const p of selectedReleasePipelineObjs) {
      pipelineNames[String(p.id)] = p.name;
    }

    setScriptCandidatesLoading(true);
    setScriptCandidatesError(null);
    setScriptCandidatesLoaded(false);

    try {
      const envRes = await getReleaseEnvironments(
        assertScmProvider(onboardingProvider),
        {
          organization: selectedOrg,
          project: selectedProject,
          releaseDefinitionIds: releaseIds,
          pipelineNames,
        },
        patToken,
      );
      const defs = envRes.definitions ?? [];
      const selections = buildReleaseSelectionsFromEnvironments(defs);
      setReleaseSelections(selections);

      const candRes = await getScriptCandidates(
        {
          organization: selectedOrg,
          project: selectedProject,
          testOrgName: testOrgValue,
          testProjectName: testProjectValue,
          testRepoUrl: testUrl,
          testBranchName: branchName,
          testPat: listPat,
          testScriptsRootPath: '/',
          testScriptsRootPaths: ['/'],
          releaseConfig: {
            organization: selectedOrg,
            project: selectedProject,
            pat: patToken,
            pipelines: selectedReleasePipelineObjs.map(p => ({
              releasePipelineId: p.id,
              releasePipelineName: p.name,
              lastReleaseId: null,
              lastReleaseProcessedAt: null,
            })),
          },
          releaseSelections: selections,
        },
        patToken,
      );

      if (!candRes.success) {
        setScriptCandidatesError(candRes.message || 'Failed to load scripts');
        setScriptCandidates([]);
        setSelectedScriptPaths(new Set());
        return;
      }

      const candidates = candRes.candidates ?? [];
      setScriptCandidates(candidates);
      setUnresolvedTcCount(candRes.unresolvedTcPaths?.length ?? 0);
      setSelectedScriptPaths(new Set());
      setScriptCandidatesLoaded(true);
    } catch (e: unknown) {
      let msg = 'Failed to load test scripts';
      const axiosErr = e as {
        response?: { data?: { detail?: unknown; message?: string } };
        message?: string;
      };
      const d = axiosErr?.response?.data;
      if (d?.detail && typeof d.detail === 'object' && 'Error' in d.detail) {
        msg = String((d.detail as { Error?: string }).Error ?? msg);
      } else if (typeof d?.detail === 'string') {
        msg = d.detail;
      } else if (d?.message) {
        msg = String(d.message);
      } else if (axiosErr?.message) {
        msg = axiosErr.message;
      }
      setScriptCandidatesError(msg);
      setScriptCandidates([]);
      setSelectedScriptPaths(new Set());
    } finally {
      setScriptCandidatesLoading(false);
    }
  }

  // ── Manual fetch handlers (replace the old debounced PAT effects) ────

  function handleFetchOrganizations() {
    if (!patValue || patValue.length < 5) return;
    resetCascade();
    resetScriptState();
    setOrgList([]);
    orgMutation.mutate(patValue);
  }

  function handleFetchTestOrganizations() {
    if (!testPatValue || testPatValue.length < 5) return;
    resetTestCascade();
    testOrgMutation.mutate(testPatValue);
  }

  // ── Manual org submission when PAT is 403 ────────────────────────────

  function handleGetProjectsManualOrg() {
    const org = orgValueRef.current;
    if (!org) return;
    setSelectedOrg(org);
    setSelectedProject('');
    setSelectedRepo('');
    setSelectedBranch('');
    setSelectedPipeline('');
    setProjects([]);
    setRepos([]);
    setBranches([]);
    setPipelines([]);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('project', '');
    setValue('repository', '');
    setValue('branch', '');
    setValue('pipeline', '');
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
    projectsMutation.mutate({ org });
  }

  function handleGetTestProjectsManualOrg() {
    const org = testOrgValueRef.current;
    if (!org) return;
    setSelectedTestOrg(org);
    setSelectedTestProject('');
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setTestProjects([]);
    setTestRepos([]);
    setTestBranches([]);
    setTestPipelines([]);
    setValue('testProject', '');
    setValue('testRepository', '');
    setValue('testBranch', '');
    setValue('testPipeline', '');
    testProjectsMutation.mutate({ org });
  }

  // ── Detect repository (form submit) ──────────────────────────────────

  function handleDetectRepository(data: Step1Values) {
    // Imperatively validate test-cascade fields when the toggle is on,
    // because Zod schema cannot read external state.
    if (testCaseRepo) {
      const requiredTest: Array<keyof Step1Values> = [
        'testPat',
        'testOrganization',
        'testProject',
        'testRepository',
        'testBranch',
        'testPipelinePlatform',
      ];
      const platform = data.testPipelinePlatform as PipelinePlatform | '';
      if (platform === 'ado') {
        requiredTest.push('testPipeline');
      } else if (platform === 'jenkins') {
        requiredTest.push(
          'jenkinsBaseUrl',
          'jenkinsUsername',
          'jenkinsApiToken',
          'jenkinsJobName',
        );
      } else if (platform === 'github-actions') {
        setError('testPipelinePlatform', {
          type: 'manual',
          message:
            'GitHub Actions CI is configured from GitHub onboarding. Choose Azure Pipelines or Jenkins here.',
        });
        return;
      }
      let firstError: keyof Step1Values | null = null;
      for (const k of requiredTest) {
        const v = data[k];
        if (typeof v === 'string' && v.length === 0) {
          setError(k as 'testPat', {
            type: 'manual',
            message: 'Required when using a separate test repository',
          });
          if (!firstError) firstError = k;
        }
      }
      if (firstError) return;

      if (!scriptCandidatesLoaded || selectedScriptPaths.size === 0) {
        setScriptCandidatesError(
          scriptCandidatesLoaded
            ? 'Select at least one test script before Detect Repository.'
            : 'Load test scripts and select files before Detect Repository.',
        );
        return;
      }
    }

    const selectedOrgObj = orgList.find(
      o => o.accountName === data.organization,
    );
    const selectedProjectObj = projects.find(p => p.name === data.project);
    const selectedRepoObj = repos.find(r => r.id === data.repository);

    const repoUrl =
      selectedRepoObj?.remoteUrl ||
      selectedRepoObj?.webUrl ||
      selectedRepoObj?.url ||
      '';
    const selectedBranchObj = branches.find(b => b.name === data.branch);
    const selectedPipelineObj =
      pipelines.find(p => String(p.id) === data.pipeline) ?? null;
    const selectedTestPipelineObj = testCaseRepo
      ? (testPipelines.find(p => String(p.id) === data.testPipeline) ?? null)
      : null;
    const selectedJenkinsJob = testCaseRepo
      ? (jenkinsJobs.find(j => j.fullName === data.jenkinsJobName) ?? null)
      : null;
    const selectedReleasePipelineObjs = releasePipelines.filter(p =>
      data.releasePipeline.includes(String(p.id)),
    );
    const pipelinePlatform = (data.testPipelinePlatform ||
      'ado') as PipelinePlatform;

    const body: DetectRepositoryBody = {
      organization: {
        accountId: selectedOrgObj?.accountId ?? orgAccountId.current,
        accountName: selectedOrgObj?.accountName ?? data.organization,
        accountUri: selectedOrgObj?.accountUri ?? '',
      },
      allProjects: [
        {
          id: selectedProjectObj?.id ?? '',
          name: selectedProjectObj?.name ?? data.project,
          repositories: repos,
        },
      ],
      selectedProjectId: data.project,
      selectedRepositoryId: data.repository,
      selectedBranch: { name: data.branch, isDefault: false },
      repoUrl,
      onboardedBy: 'developer@example.com',
      pipelineId: selectedPipelineObj?.id ?? '',
      pipelineName: selectedPipelineObj?.name ?? '',
      commitId: selectedBranchObj?.objectId ?? '',
      pipelineStageNames: [data.pipelineStage],
      testAutomation: testCaseRepo
        ? {
            location: 'different',
            testOrgName: data.testOrganization,
            testProjectName: data.testProject,
            testAdoRepoId: data.testRepository,
            testRepoUrl:
              testRepos.find(r => r.id === data.testRepository)?.remoteUrl ??
              '',
            testBranchName: data.testBranch,
            testScriptsRootPath: '/',
            testScriptsRootPaths: ['/'],
            selectedScriptPaths: Array.from(selectedScriptPaths),
            releaseSelections,
            testPipelineId:
              pipelinePlatform === 'jenkins'
                ? data.jenkinsJobName
                : String(selectedTestPipelineObj?.id ?? ''),
            testPipelineName:
              pipelinePlatform === 'jenkins'
                ? data.jenkinsJobName
                : (selectedTestPipelineObj?.name ?? ''),
            testPat: data.testPat,
            pipelinePlatform,
            ...(pipelinePlatform === 'jenkins'
              ? {
                  jenkins: {
                    baseUrl: data.jenkinsBaseUrl.trim(),
                    username: data.jenkinsUsername.trim(),
                    apiToken: data.jenkinsApiToken.trim(),
                    jobName: data.jenkinsJobName.trim(),
                    jobUrl: selectedJenkinsJob?.url,
                  },
                }
              : {}),
          }
        : {
            location: 'same',
            testOrgName: selectedOrgObj?.accountName ?? data.organization,
            testProjectName: selectedProjectObj?.name ?? data.project,
            testAdoRepoId: data.repository,
            testPipelineId: String(selectedPipelineObj?.id ?? ''),
            testPipelineName: selectedPipelineObj?.name ?? '',
            testPat: data.pat,
            pipelinePlatform: 'ado',
          },
      frontendUrl: data.frontendUrl,
      backendUrl: data.backendUrl,
      releaseConfig: {
        organization: data.organization,
        project: data.project,
        pat: data.pat,
        pipelines: selectedReleasePipelineObjs.map(p => ({
          releasePipelineId: p.id,
          releasePipelineName: p.name,
          lastReleaseId: null,
          lastReleaseProcessedAt: null,
        })),
      },
    };

    detectMutation.mutate(body, {
      onSuccess: (res: DetectRepositoryResponse) => {
        setDbRepoId(res?.onboardData?.activeRepositoryId ?? '');
        setDbBranchId(res?.onboardData?.activeBranchId ?? '');
        const repoFiles = res?.repoFiles?.data?.repoResponse?.files ?? [];
        const analysis = filterNonTestFiles(repoFiles);
        setAnalysisResult(analysis);
        setSelectedFiles(
          new Set([...analysis.nonTestFiles, ...analysis.nonTestlargerFiles]),
        );

        if (testCaseRepo) {
          const idx = res?.testScriptIndex;
          if (idx?.status === 'processing') {
            setScriptIndexStatus('processing');
            setScriptIndexMessage(
              idx.message ?? 'Indexing test scripts in background…',
            );
            setScriptIndexProgress({
              processed: 0,
              total: idx.selectedCount ?? selectedScriptPaths.size,
              percentage: 0,
              phase: 'hashing',
            });
          } else if (idx?.status === 'completed') {
            setScriptIndexStatus('completed');
            setScriptIndexMessage(idx.message ?? null);
          } else if (idx?.status === 'failed') {
            setScriptIndexStatus('failed');
            setScriptIndexMessage(idx.message ?? 'Test script indexing failed');
          } else if (idx?.status === 'skipped') {
            setScriptIndexStatus('skipped');
            setScriptIndexMessage(idx.message ?? null);
          } else {
            setScriptIndexStatus('idle');
            setScriptIndexMessage(null);
          }
        }

        setShowTechStack(true);
        setShowFileExclusions(true);
      },
    });
  }

  // ── Reset helpers ────────────────────────────────────────────────────

  function resetCascade() {
    setInvalidPatMode(false);
    setSelectedOrg('');
    setSelectedProject('');
    setSelectedRepo('');
    setSelectedBranch('');
    setSelectedPipeline('');
    setProjects([]);
    setRepos([]);
    setBranches([]);
    setPipelines([]);
    setStagesList([]);
    setReleasePipelines([]);
    setValue('organization', '');
    setValue('project', '');
    setValue('repository', '');
    setValue('branch', '');
    setValue('pipeline', '');
    setValue('pipelineStage', '');
    setValue('releasePipeline', []);
  }

  function resetTestCascade() {
    setInvalidTestPatMode(false);
    setSelectedTestOrg('');
    setSelectedTestProject('');
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setTestOrgList([]);
    setTestProjects([]);
    setTestRepos([]);
    setTestBranches([]);
    setTestPipelines([]);
    setValue('testOrganization', '');
    setValue('testProject', '');
    setValue('testRepository', '');
    setValue('testBranch', '');
    setValue('testPipeline', '');
    resetScriptState();
  }

  function handleResetAll() {
    resetForm();
    resetCascade();
    resetTestCascade();
    setOrgList([]);
    setShowCascadingSelectors(false);
    setShowTechStack(false);
    setShowFileExclusions(false);
    setTestCaseRepo(false);
    setDbRepoId('');
    setDbBranchId('');
    setAnalysisResult(null);
    setSelectedFiles(new Set());
    setPrEmails([]);
    setAutoPrEmails([]);
    setSkipPrEmails(false);
    setSkipAutoPrEmails(false);
    setPrEmailsError('');
    setAutoPrEmailsError('');
    resetScriptState();
    detectMutation.reset();
    orgMutation.reset();
    testOrgMutation.reset();
    orgAccountId.current = '';
    testOrgAccountId.current = '';
  }

  // ── Derived UI state ─────────────────────────────────────────────────

  const isBusy =
    isSubmitting ||
    orgMutation.isPending ||
    detectMutation.isPending ||
    projectsMutation.isPending ||
    reposMutation.isPending ||
    branchesMutation.isPending ||
    pipelinesMutation.isPending ||
    stagesMutation.isPending ||
    releasePipelinesMutation.isPending ||
    testOrgMutation.isPending ||
    testProjectsMutation.isPending ||
    testReposMutation.isPending ||
    testBranchesMutation.isPending ||
    testPipelinesMutation.isPending ||
    scriptCandidatesLoading;

  // Stays true from detect success until Reset; locks the Detect button and
  // cascade inputs so the user cannot re-submit without clearing first.
  const isPostDetect = showFileExclusions;

  const canLoadScripts =
    Boolean(patToken) &&
    Boolean(selectedOrg) &&
    Boolean(selectedProject) &&
    (releasePipelineValue?.length ?? 0) > 0 &&
    Boolean(testOrgValue) &&
    Boolean(testProjectValue) &&
    Boolean(testRepoValue) &&
    Boolean(testBranchValue);

  const canDetectRepository =
    testCaseRepo &&
    scriptCandidatesLoaded &&
    selectedScriptPaths.size > 0 &&
    !isPostDetect;

  const onboardStatus: string = detectMutation.isPending
    ? 'Connecting…'
    : showTechStack
      ? 'Connected'
      : orgMutation.isPending
        ? 'Fetching…'
        : showCascadingSelectors
          ? 'Select Options'
          : 'Ready';

  const statusBadgeType: 'success' | 'warning' | 'info' | 'danger' =
    onboardStatus === 'Connected'
      ? 'success'
      : onboardStatus === 'Fetching…' || onboardStatus === 'Connecting…'
        ? 'warning'
        : 'info';

  function handleSelectProvider(provider: OnboardingProvider) {
    setOnboardingProvider(provider);
    dispatch(setScmProvider(provider));
  }

  function handleChangeProvider() {
    if (onExitProvider) {
      onExitProvider();
      return;
    }
    handleResetAll();
    setOnboardingProvider(null);
  }

  // ── Render ───────────────────────────────────────────────────────────

  if (!onboardingProvider) {
    return (
      <OnboardBgWrapper>
        <OnboardingPage>
          <OnboardingPageHeader
            eyebrow="Onboarding"
            title="Add a component"
            subtitle="Select where your component is hosted to continue setup."
          />
          <OnboardingProviderSelect onSelect={handleSelectProvider} />
        </OnboardingPage>
      </OnboardBgWrapper>
    );
  }

  if (onboardingProvider === 'github') {
    return (
      <GitHubOnboardingView
        provider={onboardingProvider}
        onChangeProvider={handleChangeProvider}
        detectPrefill={
          detectPrefill?.provider === 'github' ? detectPrefill : undefined
        }
        autoConnectPat={
          connectPrefill?.provider === 'github' ? connectPrefill.pat : undefined
        }
        connectOrganization={
          connectPrefill?.provider === 'github'
            ? connectPrefill.organization
            : undefined
        }
      />
    );
  }

  return (
    <OnboardBgWrapper>
      <OnboardingPage>
        <ChangePlatformButton onClick={handleChangeProvider} />
        <OnboardingPageHeader
          eyebrow="Onboarding"
          title="Onboard new repository"
          subtitle="Connect your Azure DevOps repository, pipeline, and release configuration to start scanning."
        />

          <OnboardCard>
            <OnboardCardHeader
              title="Repository Setup"
              action={
                <StatusBadge value={onboardStatus} variant={statusBadgeType} />
              }
            />

            <OnboardCardBody>
              <form onSubmit={onSubmit} noValidate>
                <PatRow
                  action={
                    <Button
                      type="button"
                      className="h-10 shrink-0 whitespace-nowrap"
                      onClick={handleFetchOrganizations}
                      disabled={
                        isPostDetect ||
                        orgMutation.isPending ||
                        !patValue ||
                        patValue.length < 5
                      }>
                      {orgMutation.isPending
                        ? 'Fetching…'
                        : 'Fetch Organizations'}
                    </Button>
                  }>
                  {renderFields(['pat'])}
                </PatRow>

                {showCascadingSelectors && (
                  <CascadeSection locked={isPostDetect}>
                    <FormRow cols={2}>
                      {renderFields(['organization', 'project'])}
                    </FormRow>
                    <FormRow cols={3}>
                      {renderFields(['repository', 'branch', 'pipeline'])}
                    </FormRow>
                    <FormRow cols={2}>
                      {renderFields(['pipelineStage', 'releasePipeline'])}
                    </FormRow>
                  </CascadeSection>
                )}

                {showCascadingSelectors && (
                  <>
                    <SectionDivider label="Application URLs" />
                    <FormRow cols={2}>
                      {renderFields(['frontendUrl', 'backendUrl'])}
                    </FormRow>
                  </>
                )}

                {showCascadingSelectors && !testCaseRepo && !isPostDetect && (
                  <div className="mt-8 flex">
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => setTestCaseRepo(true)}>
                      Connect to Test Automation
                    </Button>
                  </div>
                )}

                {showCascadingSelectors && testCaseRepo && (
                  <TestSection>
                    <SectionDivider label="Test Automation" />
                    <PatRow
                      action={
                        <Button
                          type="button"
                          className="h-10 shrink-0 whitespace-nowrap"
                          onClick={handleFetchTestOrganizations}
                          disabled={
                            isPostDetect ||
                            testOrgMutation.isPending ||
                            !testPatValue ||
                            testPatValue.length < 5
                          }>
                          {testOrgMutation.isPending
                            ? 'Fetching…'
                            : 'Fetch Test Organizations'}
                        </Button>
                      }>
                      {renderFields(['testPat'])}
                    </PatRow>
                    <FormRow cols={2}>
                      {renderFields(['testOrganization', 'testProject'])}
                    </FormRow>
                    <FormRow cols={2}>
                      {renderFields(['testRepository', 'testBranch'])}
                    </FormRow>
                    {Boolean(testBranchValue) && (
                      <FormRow cols={2}>
                        {renderFields(['testPipelinePlatform'])}
                      </FormRow>
                    )}
                    {testPipelinePlatformValue === 'ado' && (
                      <FormRow cols={2}>
                        {renderFields(['testPipeline'])}
                      </FormRow>
                    )}
                    {testPipelinePlatformValue === 'github-actions' && (
                      <p className="m-0 text-sm text-[var(--inv-text-secondary)]">
                        GitHub Actions CI is configured from the GitHub
                        onboarding flow. Choose Azure Pipelines or Jenkins for
                        this Azure DevOps test repository.
                      </p>
                    )}
                    {testPipelinePlatformValue === 'jenkins' && (
                      <>
                        <FormRow cols={3}>
                          {renderFields([
                            'jenkinsBaseUrl',
                            'jenkinsUsername',
                            'jenkinsApiToken',
                          ])}
                        </FormRow>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={
                              isPostDetect ||
                              jenkinsJobsLoading ||
                              !jenkinsBaseUrlValue?.trim() ||
                              !jenkinsUsernameValue?.trim() ||
                              !jenkinsApiTokenValue?.trim()
                            }
                            onClick={() => void handleFetchJenkinsJobs()}>
                            {jenkinsJobsLoading
                              ? 'Fetching jobs…'
                              : 'Fetch Jenkins Jobs'}
                          </Button>
                        </div>
                        <ApiErrorAlert message={jenkinsJobsError} />
                        <FormRow cols={2}>
                          {renderFields(['jenkinsJobName'])}
                        </FormRow>
                      </>
                    )}

                    {!isPostDetect && (
                      <TestScriptLoadSection>
                        <Button
                          variant="secondary"
                          type="button"
                          disabled={isBusy || !canLoadScripts}
                          onClick={() => void handleLoadTestScripts()}>
                          {scriptCandidatesLoading
                            ? 'Loading test scripts…'
                            : 'Load test scripts'}
                        </Button>
                        {!scriptCandidatesLoaded &&
                          !scriptCandidatesLoading && (
                            <p className="my-4 text-sm text-[var(--inv-text-secondary)]">
                              Load scripts from the test repo using release
                              pipeline Katalon hints, then select files to
                              index.
                            </p>
                          )}
                        <ApiErrorAlert message={scriptCandidatesError} />
                        {(scriptCandidatesLoaded ||
                          scriptCandidatesLoading) && (
                            <TestScriptSelectionCard
                              candidates={scriptCandidates}
                              selectedPaths={selectedScriptPaths}
                              onSelectionChange={setSelectedScriptPaths}
                              loading={scriptCandidatesLoading}
                              error={null}
                              unresolvedCount={unresolvedTcCount}
                            />
                          )}
                        {canLoadScripts &&
                          !canDetectRepository &&
                          !isBusy && (
                            <p className="m-0 text-sm text-[#b42318]">
                              {scriptCandidatesLoaded
                                ? 'Select at least one test script before Detect Repository.'
                                : 'Load and select test scripts before Detect Repository.'}
                            </p>
                          )}
                      </TestScriptLoadSection>
                    )}
                  </TestSection>
                )}

                {showTechStack && analysisResult && (
                  <TechStackSection>
                    <InfoAlert>
                      Repository connected. Tech stack and test automation
                      detected.
                    </InfoAlert>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-[var(--inv-text-secondary)]">
                        Detected Language:
                      </span>
                      {analysisResult.isJavaScript && (
                        <StatusBadge value="JavaScript" variant="info-outlined" />
                      )}
                      {analysisResult.isTypeScript && (
                        <StatusBadge value="TypeScript" variant="info-outlined" />
                      )}
                      {analysisResult.isMixed && (
                        <StatusBadge
                          value="JavaScript / TypeScript"
                          variant="info-outlined"
                        />
                      )}
                      {analysisResult.isCSharp && (
                        <StatusBadge value="C#" variant="info-outlined" />
                      )}
                      {analysisResult.isPython && (
                        <StatusBadge value="Python" variant="info-outlined" />
                      )}
                      {analysisResult.isJava && (
                        <StatusBadge value="Java" variant="info-outlined" />
                      )}
                      {!analysisResult.isJavaScript &&
                        !analysisResult.isTypeScript &&
                        !analysisResult.isMixed &&
                        !analysisResult.isCSharp &&
                        !analysisResult.isPython &&
                        !analysisResult.isJava && (
                          <StatusBadge value="Other" variant="info-outlined" />
                        )}
                    </div>
                  </TechStackSection>
                )}

                <ApiErrorAlert
                  message={errMsg(
                    orgMutation.error,
                    'Failed to fetch organizations',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    projectsMutation.error,
                    'Failed to fetch projects',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    reposMutation.error,
                    'Failed to fetch repositories',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    branchesMutation.error,
                    'Failed to fetch branches',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    pipelinesMutation.error,
                    'Failed to fetch pipelines',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    stagesMutation.error,
                    'Failed to fetch pipeline stages',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    releasePipelinesMutation.error,
                    'Failed to fetch release pipelines',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    testOrgMutation.error,
                    'Failed to fetch test organizations',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    testProjectsMutation.error,
                    'Failed to fetch test projects',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    testReposMutation.error,
                    'Failed to fetch test repositories',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    testBranchesMutation.error,
                    'Failed to fetch test branches',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(
                    testPipelinesMutation.error,
                    'Failed to fetch test pipelines',
                  )}
                />
                <ApiErrorAlert
                  message={errMsg(detectMutation.error, 'Detection failed')}
                />

                <FooterButtons
                  left={
                    <>
                      {testCaseRepo && !isPostDetect && (
                        <Button type="submit" disabled={isBusy}>
                          {detectMutation.isPending
                            ? 'Detecting…'
                            : 'Detect Repository'}
                        </Button>
                      )}
                      {detectMutation.isPending && <Spinner compact />}
                    </>
                  }
                  right={
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleResetAll}>
                      Reset
                    </Button>
                  }
                />
              </form>
            </OnboardCardBody>
          </OnboardCard>

          {/* ── File exclusions + Scan cards (both render after detect) ── */}
          {showFileExclusions && analysisResult && (
            <div className="box-border flex w-full min-w-0 max-w-full flex-col gap-4">
              {testCaseRepo && scriptIndexStatus === 'processing' && (
                <ScriptIndexProgressCard
                  message={scriptIndexMessage}
                  progress={scriptIndexProgress}
                />
              )}
              {testCaseRepo && scriptIndexStatus === 'failed' && (
                <AlertNotice
                  message={
                    scriptIndexMessage ??
                    'Test script indexing failed. Fix the issue and re-run Detect Repository.'
                  }
                  status="icon-error-outline"
                />
              )}
              {testCaseRepo &&
                scriptIndexStatus === 'completed' &&
                scriptIndexMessage && (
                  <AlertNotice
                    message={scriptIndexMessage}
                    status="icon-circle-checkmark-outline"
                  />
                )}

              <div
                id="fe-card"
                ref={exclusionsRef}
                className="min-w-0 max-w-full">
                <OnboardCard secondary className="mb-0">
                  <FileExclusionsCard
                    org={selectedOrg}
                    project={selectedProject}
                    repo={
                      repos.find(r => r.id === selectedRepo)?.name ??
                      selectedRepo
                    }
                    branch={selectedBranch}
                    instrumentedFilesData={analysisResult}
                    onFilesSelected={setSelectedFiles}
                    prEmails={prEmails}
                    onPrEmailsChange={emails => {
                      setPrEmails(emails);
                      if (prEmailsError) setPrEmailsError('');
                    }}
                    autoPrEmails={autoPrEmails}
                    onAutoPrEmailsChange={emails => {
                      setAutoPrEmails(emails);
                      if (autoPrEmailsError) setAutoPrEmailsError('');
                    }}
                    skipPrEmails={skipPrEmails}
                    onSkipPrEmailsChange={val => {
                      setSkipPrEmails(val);
                      if (val) setPrEmailsError('');
                    }}
                    skipAutoPrEmails={skipAutoPrEmails}
                    onSkipAutoPrEmailsChange={val => {
                      setSkipAutoPrEmails(val);
                      if (val) setAutoPrEmailsError('');
                    }}
                    prEmailsError={prEmailsError}
                    autoPrEmailsError={autoPrEmailsError}
                  />
                </OnboardCard>
              </div>

              <OnboardCard secondary className="mb-0">
                <ScanCard
                  org={selectedOrg}
                  project={selectedProject}
                  repo={
                    repos.find(r => r.id === selectedRepo)?.name ?? selectedRepo
                  }
                  branch={selectedBranch}
                  scanBlocked={
                    testCaseRepo && scriptIndexStatus === 'processing'
                  }
                  scanBlockedMessage="Test scripts are still indexing. Please wait until indexing completes."
                  onStartScan={async () => {
                    // Validate emails before dispatching/navigating.
                    const prError =
                      !skipPrEmails && prEmails.length === 0
                        ? 'At least one reviewer email is required'
                        : '';
                    const autoPrError =
                      !skipAutoPrEmails && autoPrEmails.length === 0
                        ? 'At least one automation reviewer email is required'
                        : '';
                    setPrEmailsError(prError);
                    setAutoPrEmailsError(autoPrError);
                    if (prError || autoPrError) {
                      exclusionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      throw new Error('Please provide reviewer emails');
                    }

                    if (testCaseRepo && scriptIndexStatus === 'processing') {
                      exclusionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      throw new Error(
                        'Test scripts are still indexing. Please wait until indexing completes.',
                      );
                    }
                    if (testCaseRepo && scriptIndexStatus === 'failed') {
                      exclusionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      throw new Error(
                        'Test script indexing failed. Re-run Detect Repository before scanning.',
                      );
                    }

                    dispatch(
                      setOnboardingEmails({
                        prEmails: skipPrEmails ? [] : prEmails,
                        autoPrEmails: skipAutoPrEmails ? [] : autoPrEmails,
                      }),
                    );
                    dispatch(
                      setOnboardingBatchInputs({
                        scmProvider: assertScmProvider(onboardingProvider),
                        files: Array.from(selectedFiles),
                        patToken,
                        repoUrl: (() => {
                          // ADO may return the clone URL in any of these
                          // fields; remoteUrl can come back empty. Fall back.
                          const repo = repos.find(r => r.id === selectedRepo);
                          return (
                            repo?.remoteUrl || repo?.webUrl || repo?.url || ''
                          );
                        })(),

                        branchName: selectedBranch,
                        pipelineName:
                          pipelines.find(p => String(p.id) === selectedPipeline)
                            ?.name ?? '',
                        azureProject: selectedProject,
                        repoId: dbRepoId,
                        repoName:
                          repos.find(r => r.id === selectedRepo)?.name ??
                          selectedRepo,
                        branchId: dbBranchId,
                        org: selectedOrg,
                        testPatToken,
                        testOrg: selectedTestOrg,
                        testProject: selectedTestProject,
                        testPipelineName:
                          testPipelinePlatformValue === 'jenkins'
                            ? jenkinsJobNameValue || ''
                            : (testPipelines.find(
                                p => String(p.id) === testPipelineValue,
                              )?.name ?? ''),
                        testRepoUrl:
                          testRepos.find(r => r.id === testRepoValue)
                            ?.remoteUrl ?? '',
                        testBranchName: selectedTestBranch,
                        testPipelinePlatform:
                          (testPipelinePlatformValue as PipelinePlatform) || '',
                        jenkinsBaseUrl: jenkinsBaseUrlValue || '',
                        jenkinsUsername: jenkinsUsernameValue || '',
                        jenkinsJobName: jenkinsJobNameValue || '',
                        isFrontend: analysisResult?.isFrontend,
                      }),
                    );

                    navigate('/sia/components/process-files');
                    return true;
                  }}
                />
              </OnboardCard>
            </div>
          )}
      </OnboardingPage>
    </OnboardBgWrapper>
  );
};

export default ComponentOnboardingView;
